import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import CheckroomRoundedIcon from "@mui/icons-material/CheckroomRounded";
import ChildCareRoundedIcon from "@mui/icons-material/ChildCareRounded";
import RestaurantRoundedIcon from "@mui/icons-material/RestaurantRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import ServicePageLayout from "../components/ServicePageLayout";
import GuestIcon from "../components/GuestIcon";
import { useGuestSession } from "contexts/GuestSessionContext";
import { LUXURY } from "data/guestLuxuryTheme";

const MEAL_THEME = {
  breakfast: {
    background: "linear-gradient(128deg, #FFFDF8 0%, #F8EFD6 55%, #E8D5A3 100%)",
  },
  lunch: {
    background: "linear-gradient(128deg, #FFFDF8 0%, #F3EEE4 60%, #E8D5A3 100%)",
  },
  dinner: {
    background: "linear-gradient(128deg, #FFFEFB 0%, #F7F0E4 55%, #E8D5A3 100%)",
  },
};

const NoteRow = ({ icon: Icon, children }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.1, mt: 1.15 }}>
    <Box
      sx={{
        width: 32,
        height: 32,
        flexShrink: 0,
        borderRadius: "10px",
        bgcolor: "rgba(61,52,40,0.08)",
        color: LUXURY.surfaceInk,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Icon sx={{ fontSize: 18 }} />
    </Box>
    <Typography
      sx={{
        color: LUXURY.surfaceMuted,
        fontSize: "0.88rem",
        lineHeight: 1.5,
        pt: 0.35,
        wordBreak: "break-word",
      }}
    >
      {children}
    </Typography>
  </Box>
);

const BuffetPage = () => {
  const { portalConfig, locationLabel } = useGuestSession();
  const buffet = portalConfig.buffet;

  return (
    <ServicePageLayout
      title="Buffet"
      subtitle={`Hours and stations for ${locationLabel}. Walk in — no booking needed.`}
      illustration="buffet"
    >
      <Box
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "flex-start",
          mb: 2,
          borderRadius: "20px",
          overflow: "hidden",
          background: "linear-gradient(128deg, #FFFDF8 0%, #FFF6E4 52%, #F3E2C2 100%)",
          border: `1px solid ${LUXURY.surfaceBorder}`,
          boxShadow: "0 12px 28px rgba(0,0,0,0.12)",
        }}
      >
        <Box sx={{ position: "relative", flex: 1, minWidth: 0, p: 2.2, pr: 1.5 }}>
          <Typography
            sx={{
              fontSize: "0.64rem",
              fontWeight: 800,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: LUXURY.surfaceLabel,
            }}
          >
            Dining room
          </Typography>
          <Box sx={{ width: 28, height: 3, mt: 0.8, mb: 1.05, borderRadius: 99, bgcolor: "#C9A66B" }} />
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.8 }}>
            <PlaceRoundedIcon
              sx={{ fontSize: 18, color: LUXURY.surfaceInk, mt: 0.2, flexShrink: 0 }}
            />
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: "1.05rem",
                color: LUXURY.surfaceInk,
                lineHeight: 1.3,
                wordBreak: "break-word",
              }}
            >
              {buffet.location}
            </Typography>
          </Box>
          <Box
            sx={{
              mt: 1.2,
              display: "inline-flex",
              alignItems: "center",
              gap: 0.6,
              minHeight: 32,
              px: 1.2,
              borderRadius: 999,
              bgcolor: "rgba(61,52,40,0.08)",
              border: `1px solid ${LUXURY.surfaceBorder}`,
              color: LUXURY.surfaceInk,
              fontSize: "0.78rem",
              fontWeight: 800,
            }}
          >
            <CheckroomRoundedIcon sx={{ fontSize: 16 }} />
            {buffet.dress}
          </Box>
          <NoteRow icon={ChildCareRoundedIcon}>{buffet.kidsNote}</NoteRow>
          <NoteRow icon={RestaurantRoundedIcon}>{buffet.dietaryNote}</NoteRow>
        </Box>
        <Box sx={{ pr: 2, pt: 2.2, flexShrink: 0 }}>
          <GuestIcon name="buffet" size={72} variant="mark" />
        </Box>
      </Box>

      <Stack spacing={1.5}>
        {(buffet.meals || []).map((meal) => {
          const theme = MEAL_THEME[meal.id] || MEAL_THEME.breakfast;
          return (
            <Box
              key={meal.id}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                borderRadius: "20px",
                overflow: "hidden",
                background: theme.background,
                border: `1px solid ${LUXURY.surfaceBorder}`,
                boxShadow: "0 10px 24px rgba(0,0,0,0.08)",
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0, p: 2.1, pr: 1.2 }}>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.55,
                    minHeight: 28,
                    px: 1.1,
                    borderRadius: 999,
                    bgcolor: LUXURY.surfaceInk,
                    color: "#fff",
                    fontSize: "0.72rem",
                    fontWeight: 800,
                  }}
                >
                  <ScheduleRoundedIcon sx={{ fontSize: 14 }} />
                  {meal.hours}
                </Box>
                <Typography
                  sx={{
                    mt: 1.15,
                    fontWeight: 800,
                    fontSize: "1.12rem",
                    color: LUXURY.surfaceInk,
                    letterSpacing: "-0.03em",
                    lineHeight: 1.25,
                    wordBreak: "break-word",
                  }}
                >
                  {meal.title}
                </Typography>
                <Box sx={{ mt: 1.25, display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                  {(meal.highlights || []).map((item) => (
                    <Box
                      key={item}
                      sx={{
                        px: 1.15,
                        py: 0.55,
                        borderRadius: 999,
                        bgcolor: LUXURY.surfaceChipBg,
                        border: `1px solid ${LUXURY.surfaceBorder}`,
                        color: LUXURY.surfaceChipText,
                        fontSize: "0.74rem",
                        fontWeight: 700,
                        lineHeight: 1.25,
                      }}
                    >
                      {item}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box sx={{ pr: 1.6, pt: 1.8, flexShrink: 0 }}>
                <GuestIcon name={meal.id} size={56} variant="mark" />
              </Box>
            </Box>
          );
        })}
      </Stack>
    </ServicePageLayout>
  );
};

export default BuffetPage;
